import React, {useEffect, useMemo} from 'react';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {CustomFieldResourceTypesEnum} from '../../../models/api/customFieldModel';
import {useBaseEditorContext} from '../../editors/baseEditorContext';
import {getEditorErrorLabel} from '../../editors/commons/editorErrors';
import {useExpressionsContext} from '../../editors/expressions/expressionsContext';
import {RuleDefaultCardTypesEnum} from '../ruleActionConditionHelpers';
import {getConditionErrors} from '../ruleConditionHelpers';
import {ClientRuleCondition, RuleCondition, RuleConditionTypesEnum} from '../ruleConditionModel';
import {RuleConditionDefinition} from '../ruleParameterModel';
import {RuleAttachmentNameConditionCard} from './cards/ruleAttachmentNameConditionCard';
import {RuleCustomFieldConditionCard} from './cards/ruleCustomFieldConditionCard';
import {RuleDateIsConditionCard} from './cards/ruleDateIsConditionCard';
import {RuleDefaultActionConditionCard} from './cards/ruleDefaultActionConditionCard';
import {RuleExpressionConditionCard} from './cards/ruleExpressionConditionCard';
import {RuleMessageBodyConditionCard} from './cards/ruleMessageBodyConditionCard';
import {RuleRecipientFieldConditionCard} from './cards/ruleRecipientFieldConditionCard';
import {RuleUnrepliedAfterConditionCard} from './cards/ruleUnrepliedAfterConditionCard';
import {EditorStateActions} from './hooks/useEditorState';

/*
 * Types.
 */

export interface RuleConditionCardProps {
  condition: ClientRuleCondition;
  conditionDefinition: RuleConditionDefinition | undefined;
  shouldHideError?: boolean;

  onUpdateCondition: HandlerOf<RuleCondition>;
  onRemoveCondition?: Handler;
  onValidation: EditorStateActions['onValidation'];
}

/*
 * Component.
 */

export const RuleConditionCard = (props: RuleConditionCardProps) => {
  const {
    condition,
    conditionDefinition,
    shouldHideError,
    onUpdateCondition,
    onRemoveCondition,
    onValidation,
  } = props;
  const {isReadOnly, hasHighlightErrorsFlag} = useBaseEditorContext();
  const {expressions} = useExpressionsContext();

  const localize = useLocalize();

  const maybeError = useMemo(
    () =>
      hasHighlightErrorsFlag ? getConditionErrors(condition, conditionDefinition, expressions)[0] : undefined,
    [hasHighlightErrorsFlag, condition, conditionDefinition, expressions],
  );

  useEffect(() => onValidation(condition.clientId, maybeError), [onValidation, condition, maybeError]);

  const errorMessage = maybeError && !shouldHideError ? localize(getEditorErrorLabel(maybeError)) : undefined;

  switch (condition.type) {
    /**
     * Custom attributes all share the same component,
     * and switching from any one of them to another is done in that component (not in the condition picker).
     */
    case RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_NUMBER:
    case RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_STRING:
    case RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_BOOLEAN:
    case RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_DATETIME:
    case RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_TEAMMATE:
      return (
        <RuleCustomFieldConditionCard
          condition={condition}
          conditionDefinition={conditionDefinition}
          customFieldResourceType={CustomFieldResourceTypesEnum.CARD}
          errorMessage={errorMessage}
          onUpdate={onUpdateCondition}
          onDelete={onRemoveCondition}
          isDisabled={isReadOnly}
        />
      );
    case RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_NUMBER:
    case RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_STRING:
    case RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_BOOLEAN:
    case RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_DATETIME:
    case RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_TEAMMATE:
      return (
        <RuleCustomFieldConditionCard
          condition={condition}
          conditionDefinition={conditionDefinition}
          customFieldResourceType={CustomFieldResourceTypesEnum.ACCOUNT}
          errorMessage={errorMessage}
          onUpdate={onUpdateCondition}
          onDelete={onRemoveCondition}
          isDisabled={isReadOnly}
        />
      );

    // All recipient field filters are also contained in a unique card.
    case RuleConditionTypesEnum.MESSAGE_TO:
    case RuleConditionTypesEnum.MESSAGE_FROM:
    case RuleConditionTypesEnum.MESSAGE_CC:
    case RuleConditionTypesEnum.MESSAGE_BCC:
    case RuleConditionTypesEnum.MESSAGE_TO_CC:
    case RuleConditionTypesEnum.MESSAGE_ANY_RECIPIENT:
      return (
        <RuleRecipientFieldConditionCard
          condition={condition}
          conditionDefinition={conditionDefinition}
          errorMessage={errorMessage}
          onUpdate={onUpdateCondition}
          onDelete={onRemoveCondition}
          isDisabled={isReadOnly}
        />
      );

    case RuleConditionTypesEnum.COMPARE_EXPRESSION_BOOLEAN:
    case RuleConditionTypesEnum.COMPARE_EXPRESSION_NUMBER:
    case RuleConditionTypesEnum.COMPARE_EXPRESSION_DATETIME:
    case RuleConditionTypesEnum.COMPARE_EXPRESSION_STRING:
    case RuleConditionTypesEnum.COMPARE_EXPRESSION_TEAMMATE:
    case RuleConditionTypesEnum.COMPARE_EXPRESSION_LIST:
    case RuleConditionTypesEnum.COMPARE_EXPRESSION_OTHER:
      return (
        <RuleExpressionConditionCard
          condition={condition}
          conditionDefinition={conditionDefinition}
          errorMessage={errorMessage}
          onUpdate={onUpdateCondition}
          onDelete={onRemoveCondition}
          isDisabled={isReadOnly}
        />
      );

    case RuleConditionTypesEnum.MESSAGE_BODY:
    case RuleConditionTypesEnum.MESSAGE_HTML_BODY:
      return (
        <RuleMessageBodyConditionCard
          condition={condition}
          conditionDefinition={conditionDefinition}
          errorMessage={errorMessage}
          onUpdate={onUpdateCondition}
          onDelete={onRemoveCondition}
          isDisabled={isReadOnly}
        />
      );

    case RuleConditionTypesEnum.UNREPLIED_AFTER:
      return (
        <RuleUnrepliedAfterConditionCard
          condition={condition}
          conditionDefinition={conditionDefinition}
          errorMessage={errorMessage}
          onUpdate={onUpdateCondition}
          onDelete={onRemoveCondition}
          isDisabled={isReadOnly}
        />
      );

    case RuleConditionTypesEnum.DATE_IS:
      return (
        <RuleDateIsConditionCard
          condition={condition}
          conditionDefinition={conditionDefinition}
          errorMessage={errorMessage}
          onUpdate={onUpdateCondition}
          onDelete={onRemoveCondition}
          isDisabled={isReadOnly}
        />
      );

    case RuleConditionTypesEnum.ATTACHMENT_NAME_CONTAINS:
      return (
        <RuleAttachmentNameConditionCard
          condition={condition}
          conditionDefinition={conditionDefinition}
          errorMessage={errorMessage}
          onUpdate={onUpdateCondition}
          onDelete={onRemoveCondition}
          isDisabled={isReadOnly}
        />
      );

    // Use generic rendering for most conditions.
    default:
      return (
        <RuleDefaultActionConditionCard
          conditionOrAction={condition}
          conditionOrActionDefinition={conditionDefinition}
          type={RuleDefaultCardTypesEnum.CONDITION}
          errorMessage={errorMessage}
          onUpdate={onUpdateCondition}
          onDelete={onRemoveCondition}
          isDisabled={isReadOnly}
        />
      );
  }
};
